import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as R from "ramda";
import { useCallback } from "react";

import {
  createPromptTemplate,
  deletePromptTemplate,
  fetchPromptTemplateById,
  fetchPromptTemplates,
  togglePromptTemplateStatus,
  updatePromptTemplate
} from "../../../data/queries/promptTemplates";
import {
  PromptTemplate,
  PromptTemplateCategory,
  PromptTemplateCreationParams,
  PromptTemplateResponse,
  PromptTemplateScope,
  PromptTemplateUpdateParams,
  SinglePromptTemplateResponse
} from "../../../types/prompt-template";

interface UsePromptTemplatesOptions {
  enabledOnly?: boolean;
  autoFetch?: boolean;
  category?: PromptTemplateCategory;
  scope?: PromptTemplateScope;
}

interface UsePromptTemplatesReturn {
  templates: PromptTemplate[];
  loading: boolean;
  error: Error | null;
  fetchTemplates: () => Promise<void>;
  fetchTemplateById: (id: string) => Promise<PromptTemplate | null | undefined>;
  createTemplate: (template: PromptTemplateCreationParams) => Promise<PromptTemplate | null | undefined>;
  updateTemplate: (id: string, updates: PromptTemplateUpdateParams) => Promise<PromptTemplate | null | undefined>;
  deleteTemplate: (id: string) => Promise<boolean>;
  toggleTemplateStatus: (id: string, enabled: boolean) => Promise<PromptTemplate | null | undefined>;
}

const safeAsync = async <T>(fn: () => Promise<T>, fallback: T): Promise<T> => {
  try {
    return await fn();
  } catch {
    return fallback;
  }
};

const QUERY_KEY = "promptTemplates";

const filterByScope = (templates: PromptTemplate[], scope?: PromptTemplateScope): PromptTemplate[] => {
  if (!scope || R.isEmpty(templates)) {
    return templates;
  }
  return templates.filter(template => template.metadata.scope?.includes(scope));
};

const filterByCategory = (templates: PromptTemplate[], category?: PromptTemplateCategory): PromptTemplate[] => {
  if (!category || R.isEmpty(templates)) {
    return templates;
  }
  return templates.filter(template => template.metadata.category === category);
};

const unwrapSingle = (response: SinglePromptTemplateResponse): PromptTemplate | null => {
  if (response.error) {
    throw response.error;
  }
  return response.data;
};

const unwrapList = (response: PromptTemplateResponse): PromptTemplate[] => {
  if (response.error) {
    throw response.error;
  }
  return response.data || [];
};

const useTemplateMutations = (invalidate: () => Promise<void>): Pick<
  UsePromptTemplatesReturn,
  "fetchTemplateById" | "createTemplate" | "updateTemplate" | "deleteTemplate" | "toggleTemplateStatus"
> => {
  const fetchByIdMutation = useMutation({
    mutationFn: async (id: string) => unwrapSingle(await fetchPromptTemplateById(id))
  });

  const createMutation = useMutation({
    mutationFn: async (template: PromptTemplateCreationParams) => unwrapSingle(await createPromptTemplate(template)),
    onSuccess: () => invalidate()
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: PromptTemplateUpdateParams }) =>
      unwrapSingle(await updatePromptTemplate(id, updates)),
    onSuccess: () => invalidate()
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await deletePromptTemplate(id);
      if (response.error) {
        throw response.error;
      }
      return true;
    },
    onSuccess: () => invalidate()
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) =>
      unwrapSingle(await togglePromptTemplateStatus(id, enabled)),
    onSuccess: () => invalidate()
  });

  return {
    fetchTemplateById: async (id) =>
      safeAsync(() => fetchByIdMutation.mutateAsync(id), null),
    createTemplate: async (template) =>
      safeAsync(() => createMutation.mutateAsync(template), null),
    updateTemplate: async (id, updates) =>
      safeAsync(() => updateMutation.mutateAsync({ id, updates }), null),
    deleteTemplate: async (id) =>
      safeAsync(() => deleteMutation.mutateAsync(id), false),
    toggleTemplateStatus: async (id, enabled) =>
      safeAsync(() => toggleMutation.mutateAsync({ id, enabled }), null)
  };
};

const usePromptTemplates = (options: UsePromptTemplatesOptions = {}): UsePromptTemplatesReturn => {
  const { enabledOnly = false, autoFetch = true, category, scope } = options;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [QUERY_KEY, enabledOnly, category, scope],
    queryFn: async () => filterByCategory(filterByScope(unwrapList(await fetchPromptTemplates(enabledOnly)), scope), category),
    enabled: autoFetch
  });

  const invalidate = useCallback(
    (): Promise<void> => queryClient.invalidateQueries({ queryKey: [QUERY_KEY] }),
    [queryClient]
  );

  const mutations = useTemplateMutations(invalidate);

  return {
    templates: query.data ?? [],
    loading: query.isFetching,
    error: query.error as Error | null,
    fetchTemplates: async (): Promise<void> => {
      await invalidate();
    },
    ...mutations
  };
};

export default usePromptTemplates;
